import { IconButton, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { InfoModal } from '@/commons/info-modal';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { CreateAdFormData } from '@/pages/ad-pages/CreateAd';
import { Card } from '@/features/card';

type Props = {
  control: Control<CreateAdFormData>;
  errors: FieldErrors<CreateAdFormData>;
  valueFrom: Date | null;
  valueTo: Date | null;
  displays?: number;
  views?: number;
  setValueFrom: (value: Date | null) => void;
  setValueTo: (value: Date | null) => void;
};

export const StartEndDateSection: FC<Props> = ({
  control,
  valueFrom,
  valueTo,
  setValueFrom,
  setValueTo,
  displays,
  views,
}) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const { t } = useTranslation();

  const handleFromChange = (newValue: Dayjs | null) => {
    if (newValue) {
      // Set time to 00:00:00 for start date
      const startOfDay = newValue.startOf('day');
      setValueFrom(startOfDay.toDate());

      // If end date is before start date + 1 day, reset it to start date + 1 day
      const minEndDate = startOfDay.add(1, 'day');
      if (valueTo && dayjs(valueTo).isBefore(minEndDate)) {
        setValueTo(minEndDate.toDate());
      }
    } else {
      setValueFrom(null);
    }
  };

  const handleToChange = (newValue: Dayjs | null) => {
    if (newValue) {
      // Set time to 23:59:59 for end date
      const endOfDay = newValue.endOf('day');
      setValueTo(endOfDay.toDate());
    } else {
      setValueTo(null);
    }
  };

  const totalDays = useMemo(
    () =>
      valueFrom && valueTo
        ? dayjs(valueTo).diff(dayjs(valueFrom), 'day') > 0
          ? dayjs(valueTo).diff(dayjs(valueFrom), 'day')
          : 0
        : 0,
    [valueFrom, valueTo]
  );

  const average = useMemo(() => {
    let result = {
      title: '',
      value: '0',
    };

    if (displays) {
      result = {
        title: t('start_end_date_section.average_displays'),
        value:
          totalDays > 0 && displays > 0
            ? `~${Math.ceil(displays / totalDays)}`
            : '0',
      };
    }
    if (views) {
      result = {
        title: t('start_end_date_section.average_views'),
        value:
          totalDays > 0 && views > 0 ? `~${Math.ceil(views / totalDays)}` : '0',
      };
    }

    return result;
  }, [displays, views, totalDays, t]);

  return (
    <div className="w-full flex flex-col">
      <Typography variant="h6" sx={{ mb: '8px' }}>
        <Trans i18nKey="start_end_date_section.title" />
        <IconButton size="medium" onClick={() => setInfoModalOpen(true)}>
          <InfoIcon fontSize="inherit" />
        </IconButton>
      </Typography>

      <Card className="flex flex-col w-full md:flex-row">
        <div className="flex items-center justify-center w-full">
          <div className="flex items-center justify-center flex-1/2">
            <Controller
              name="startDate"
              defaultValue={new Date()}
              control={control}
              render={({ field: { onChange, value } }) => {
                const handleChange = (value: Dayjs | null) => {
                  onChange(value);
                  handleFromChange(value);
                };

                return (
                  <DatePicker
                    label={t('date_time_picker.from')}
                    value={value ? dayjs(value) : null}
                    onChange={handleChange}
                    disablePast
                    sx={{ flexGrow: 1 }}
                  />
                );
              }}
            />

            <div className="mx-4">
              <ArrowForwardIcon />
            </div>

            <Controller
              name="endDate"
              defaultValue={dayjs().add(1, 'day').toDate()}
              control={control}
              render={({ field: { onChange, value } }) => {
                const handleChange = (value: Dayjs | null) => {
                  onChange(value);
                  handleToChange(value);
                };

                return (
                  <DatePicker
                    label={t('date_time_picker.to')}
                    value={value ? dayjs(value) : null}
                    onChange={handleChange}
                    disablePast
                    minDate={
                      valueFrom ? dayjs(valueFrom).add(1, 'day') : undefined
                    }
                    sx={{ flexGrow: 1 }}
                  />
                );
              }}
            />
          </div>

          <div className="flex items-center justify-center flex-col mx-4 flex-1/4 text-center h-full">
            <span className="mr-2 text-sm">
              <Trans i18nKey="start_end_date_section.total_days" />:
            </span>
            <span className="font-bold text-xl">{totalDays}</span>
          </div>

          <div className="flex items-center justify-center flex-col mx-4 flex-1/4 border-l border-gray-200 pl-4 h-full">
            <span className="mr-2 text-sm text-center">{average.title}</span>
            <span className="font-bold text-xl">{average.value}</span>
          </div>
        </div>
        <InfoModal
          title={t('start_end_date_section.title')}
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
        >
          <Trans i18nKey="start_end_date_section.main_info_content" />
        </InfoModal>
      </Card>
    </div>
  );
};
